/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

interface UrlParamsContextType {
  params: Record<string, any>;
  filters: Record<string, any>;
  updateParam: (_key: string, _value: any) => void;
  updateUrlParams: (_data: Record<string, any>) => void;
  removeParam: (_key: string | string[], _index?: number) => void;
  resetFilters: () => void;
  resetSpecificFilters: (_keysToPreserve: string[]) => void;
}

interface UrlParamsProviderProps {
  children: React.ReactNode;
  initialValues?: Record<string, any>;
  excludeFields?: string[];
}

const UrlParamsContext = createContext<UrlParamsContextType | undefined>(undefined);

// Helper function to parse URL parameter values
const parseParamValue = (value: string): any => {
  // Single value parsing
  const numValue = Number(value);
  // Don't convert to number if the string has leading zeros (like '001', '0123')
  // or if converting would change the string representation
  if (!isNaN(numValue) && value !== '' && String(numValue) === value) return numValue;
  if (value === 'true' || value === 'false') return value === 'true';
  return value;
};

// Helper function to parse indexed array parameters
const parseIndexedParams = (searchParams: URLSearchParams): Record<string, any> => {
  const result: Record<string, any> = {};
  const arrayParams: Record<string, any[]> = {};

  searchParams.forEach((value, key) => {
    // Check if key has indexed format like 'type[0]'
    const indexMatch = key.match(/^(.+)\[(\d+)\]$/);
    if (indexMatch) {
      const [, baseKey, indexStr] = indexMatch;
      const index = parseInt(indexStr, 10);
      if (!arrayParams[baseKey]) {
        arrayParams[baseKey] = [];
      }
      arrayParams[baseKey][index] = parseParamValue(value);
    } else {
      result[key] = parseParamValue(value);
    }
  });

  // Convert sparse arrays to dense arrays and add to result
  Object.entries(arrayParams).forEach(([key, array]) => {
    result[key] = array.filter(item => item !== undefined);
  });

  return result;
};

export const UrlParamsProvider: React.FC<UrlParamsProviderProps> = ({
  children,
  initialValues = { page: 1, limit: 20 },
  excludeFields = [],
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Compute params by merging initial values with URL params
  const params = useMemo(() => {
    const result = { ...initialValues };
    const parsedParams = parseIndexedParams(searchParams);

    Object.entries(parsedParams).forEach(([key, value]) => {
      if (!excludeFields.includes(key) && value !== '') {
        result[key] = value;
      }
    });

    return result;
  }, [searchParams, initialValues, excludeFields]);

  // Helper function to update URL parameters
  const updateSearchParams = useCallback(
    (updater: (_params: URLSearchParams) => void) => {
      const newParams = new URLSearchParams(searchParams);
      updater(newParams);
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const updateUrlParams = useCallback(
    (data: Record<string, any>) => {
      updateSearchParams(newParams => {
        Object.entries(data).forEach(([key, value]) => {
          if (excludeFields.includes(key)) return;

          // Remove existing parameters for this key (both single and indexed)
          const keysToRemove = Array.from(newParams.keys()).filter(
            paramKey => paramKey === key || paramKey.startsWith(`${key}[`)
          );
          keysToRemove.forEach(paramKey => newParams.delete(paramKey));

          if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
            // Value is empty, already removed above
            return;
          } else if (Array.isArray(value)) {
            // Handle array values with indexed parameters
            value.forEach((item, index) => {
              // Check if the item is a date object or dayjs object and format it
              let formattedItem = item;
              if (item && (dayjs.isDayjs(item) || item instanceof Date)) {
                const date = dayjs(item);
                formattedItem = date.isValid() ? date.format('YYYY-MM-DD') : item;
              }
              newParams.set(`${key}[${index}]`, String(formattedItem));
            });
          } else {
            // Check if the value is a date object or dayjs object and format it
            let formattedValue = value;
            if (value && (dayjs.isDayjs(value) || value instanceof Date)) {
              const date = dayjs(value);
              formattedValue = date.isValid() ? date.format('YYYY-MM-DD') : value;
            }
            newParams.set(key, String(formattedValue));
          }
        });
      });
    },
    [updateSearchParams, excludeFields]
  );

  const updateParam = useCallback(
    (key: string, value: any) => {
      updateUrlParams({ [key]: value });
    },
    [updateUrlParams]
  );

  const removeParam = useCallback(
    (key: string | string[], index?: number) => {
      updateSearchParams(newParams => {
        if (Array.isArray(key)) {
          key.forEach(k => {
            // Remove both single and indexed parameters
            const keysToRemove = Array.from(newParams.keys()).filter(
              paramKey => paramKey === k || paramKey.startsWith(`${k}[`)
            );
            keysToRemove.forEach(paramKey => newParams.delete(paramKey));
          });
        } else if (typeof index === 'number') {
          // Remove specific indexed parameter and reindex remaining ones
          const indexedKeys = Array.from(newParams.keys())
            .filter(paramKey => paramKey.startsWith(`${key}[`))
            .sort((a, b) => {
              const aIndex = parseInt(a.match(/\[(\d+)\]$/)?.[1] || '0', 10);
              const bIndex = parseInt(b.match(/\[(\d+)\]$/)?.[1] || '0', 10);
              return aIndex - bIndex;
            });

          const values = indexedKeys.map(paramKey => newParams.get(paramKey));

          // Remove all indexed parameters
          indexedKeys.forEach(paramKey => newParams.delete(paramKey));

          // Remove the specified index and reindex
          values.splice(index, 1);

          // Re-add remaining values with correct indices
          values.forEach((value, newIndex) => {
            if (value !== null) {
              newParams.set(`${key}[${newIndex}]`, value);
            }
          });
        } else {
          // Remove both single and indexed parameters
          const keysToRemove = Array.from(newParams.keys()).filter(
            paramKey => paramKey === key || paramKey.startsWith(`${key}[`)
          );
          keysToRemove.forEach(paramKey => newParams.delete(paramKey));
        }
      });
    },
    [updateSearchParams]
  );

  const resetFilters = useCallback(() => {
    updateSearchParams(newParams => {
      // Clear all parameters
      Array.from(newParams.keys()).forEach(key => newParams.delete(key));
      newParams.set('page', String(initialValues.page || 1));
      newParams.set('limit', String(initialValues.limit || 20));
    });
  }, [updateSearchParams, initialValues]);

  const resetSpecificFilters = useCallback(
    (keysToPreserve: string[]) => {
      updateSearchParams(newParams => {
        // Get all current keys to preserve
        const keysToKeep = new Set(keysToPreserve);

        // Clear all parameters except those in keysToKeep and page/limit
        Array.from(newParams.keys()).forEach(key => {
          // Extract base key from indexed parameters (e.g., 'type[0]' -> 'type')
          const baseKey = key.match(/^(.+)\[\d+\]$/)?.[1] || key;

          // Keep the key if it's in preserve list, or is page/limit
          if (keysToKeep.has(baseKey) || ['page', 'limit'].includes(baseKey)) {
            return; // Don't delete
          }

          newParams.delete(key);
        });

        // Reset page to initial value
        newParams.set('page', String(initialValues.page || 1));
      });
    },
    [updateSearchParams, initialValues]
  );

  const contextValue = useMemo(
    () => ({
      params,
      filters: Object.fromEntries(Object.entries(params).filter(([key]) => !['page', 'limit'].includes(key))),
      updateParam,
      updateUrlParams,
      removeParam,
      resetFilters,
      resetSpecificFilters,
    }),
    [params, updateParam, updateUrlParams, removeParam, resetFilters, resetSpecificFilters]
  );

  return <UrlParamsContext.Provider value={contextValue}>{children}</UrlParamsContext.Provider>;
};

export const useUrlParamsContext = (): UrlParamsContextType => {
  const context = useContext(UrlParamsContext);
  if (context === undefined) {
    throw new Error('useUrlParamsContext must be used within a UrlParamsProvider');
  }
  return context;
};
